import * as React from 'react'
import {
  setAttachClientToPartnerProfile,
  useAttachClientToPartnerProfile,
} from '../../../ib/partners/attach'
import { useEffect, useState } from 'react'
import { useStore, useUnit } from 'effector-react'
import { $userRole } from '../../../../features/user'
import {
  $errorClientProfileRequest,
  partnerMounted,
  setClientProfileRequestError,
  submitEditAutoWithdrawalStatus,
  updateAutoWithdrawalStatus,
} from '../../details/model'
import { ErrorScreen } from '../../../../shared/components/errorScreen/errorScreen'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  SettingOutlined,
  CommentOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import { Button, Flex, Tag, Typography } from 'antd'
import { path } from '../../../../libs'
import { AttachPartnerToClientModal } from '../../atoms/AttachPartnerToClientModal'
import { ConfirmDetachModal } from '../../atoms/ConfirmDetachModal'
import { submitDetachClientFromPartner } from '../../atoms/ConfirmDetachModal/models'
import { CommentList } from '../../../../views/widgets/commentList'
import { CommentForm } from '../../../../views/widgets/commentForm'
import { Client } from '../../details'
import { RequestError } from '../../../../shared/errorResponse'
import { Partner } from '../../../ib/partners/details/types'
import { $errorDetachClientToPartner } from '../../atoms/ConfirmDetachModal/models'
import { Guard } from '../../../../features/session/guard'
import { Edit2FAModal } from '../edit2FAModal/component'
import {
  GUARD_RULES,
  getRolesByGuardRules,
  PERMISSIONS,
} from '../../../../features/session/constants'
import { role as roleLib } from '../../../../libs/role'
import dayjs from 'dayjs'
import { $countryNameByCode } from '../../../../features/dictionaries/model'

const { Text } = Typography

function PartnerIdLink({ partnerId }: { partnerId: string }) {
  const userRole = useStore($userRole)
  const canView =
    userRole &&
    userRole.includes(roleLib.logged()) &&
    getRolesByGuardRules(GUARD_RULES.VIEW_PARTNER_PROFILE).some((r) =>
      userRole.includes(r),
    )

  if (canView) {
    return (
      <a href={path.ib.partners.details(partnerId)} target="_blank">
        {partnerId}
      </a>
    )
  }
  return <span>{partnerId}</span>
}

type Props = {
  className?: string
  client: Client
  partner: {
    id: string
    firstName: string
  }
  partnerError: RequestError | null
  fullPartner?: Partner | null
}

function InfoRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <Flex
      align="center"
      style={{
        padding: '6px 0',
        borderBottom: '1px solid var(--crm-border)',
      }}
    >
      <Text
        type="secondary"
        style={{ fontSize: 16, flexShrink: 0, width: 150 }}
      >
        {label}
      </Text>
      {value === null || value === undefined || value === '' ? (
        <Text type="secondary" style={{ fontSize: 16 }}>
          —
        </Text>
      ) : (
        <Text
          style={{
            fontSize: 16,
            textAlign: 'right',
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Text>
      )}
    </Flex>
  )
}

function StatusItem({
  label,
  value,
  color,
  extra,
}: {
  label: string
  value: string
  color: string
  extra?: React.ReactNode
}) {
  return (
    <div
      style={{
        background: 'var(--crm-bg-elevated)',
        border: '1px solid var(--crm-border)',
        borderRadius: 8,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <Text
        type="secondary"
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </Text>
      <Tag
        color={color}
        style={{ width: 'fit-content', fontSize: 11, fontWeight: 600 }}
      >
        {value}
      </Tag>
      {extra}
    </div>
  )
}

function Section({
  icon,
  title,
  children,
  extra,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  extra?: React.ReactNode
}) {
  return (
    <div
      style={{
        background: 'var(--crm-bg)',
        border: '1px solid var(--crm-border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <Flex
        align="center"
        gap={8}
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--crm-border)',
        }}
      >
        {icon}
        <Text strong style={{ fontSize: 13 }}>
          {title}
        </Text>
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </Flex>
      <div style={{ padding: '4px 16px 12px' }}>{children}</div>
    </div>
  )
}

export const ClientProfile = ({
  className,
  client,
  partner,
  partnerError,
  fullPartner,
}: Props) => {
  let { attachClientToPartnerProfile, errorAttachClientToPartner } =
    useAttachClientToPartnerProfile()

  const [isOpenModal, setOpenModal] = useState({
    isAttachModalOpen: false,
    isDetachModalOpen: false,
  })

  const currentOriginHost = window.location.origin

  const handleCopyClientInfo = () => {
    const clientUrl = `${currentOriginHost}/client/${client.clientId}`
    const plainText = `
Client name: ${client.fullName}
Client ID: ${client.clientId}
Email: ${client.email}
`.trim()
    const htmlText = `
<b>Client name:</b> ${client.fullName}
<b>Client ID:</b> <a href="${clientUrl}">${client.clientId}</a>
<b>Email:</b> ${client.email}
`.trim()
    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      }),
    ])
  }

  const countryNameByCode = useStore($countryNameByCode)
  const loadingAutoWDStatus = useStore(updateAutoWithdrawalStatus.pending)
  const errorDetachClientToPartner = useStore($errorDetachClientToPartner)
  const errorClientProfileRequest = useUnit($errorClientProfileRequest)

  useEffect(() => {
    setAttachClientToPartnerProfile({
      ...attachClientToPartnerProfile,
      clientUserId: client.clientId,
    })
  }, [client.clientId])

  const toggleModals = (name: string, value: boolean) => {
    setOpenModal((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  if (
    ![0, 404].includes(errorClientProfileRequest.code) ||
    ![0, 404, null].includes(partnerError?.code || null)
  ) {
    return (
      <ErrorScreen
        showRetry
        onRetry={() => {
          if (errorClientProfileRequest.code !== 0) {
            setAttachClientToPartnerProfile({
              ...attachClientToPartnerProfile,
              clientUserId: client.clientId,
            })
            setClientProfileRequestError({
              message: '',
              code: 0,
            })
          }
          if (partnerError?.code !== 0) {
            partnerMounted()
          }
        }}
      />
    )
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 500px',
        gap: 16,
        alignItems: 'start',
      }}
    >
      {/* LEFT COLUMN */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* PERSONAL INFORMATION */}
        <Section
          icon={<UserOutlined style={{ color: 'var(--crm-accent-blue, #3b82f6)' }} />}
          title="Personal Information"
          extra={
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopyClientInfo}
            >
              Copy info
            </Button>
          }
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0 32px',
            }}
          >
            <div>
              <InfoRow label="First Name" value={client.firstName} />
              <InfoRow label="Middle Name" value={client.middleName} />
              <InfoRow label="Last Name" value={client.lastName} />
              <InfoRow
                label="Country"
                value={
                  <Flex align="center" gap={4}>
                    <span style={{ fontSize: 12 }}>
                      {countryNameByCode[client.country] || client.country}
                    </span>
                    {client.verified ? (
                      <CheckCircleOutlined
                        style={{ color: 'var(--crm-accent-green, #22c55e)', fontSize: 11 }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{ color: 'var(--crm-accent-red, #ef4444)', fontSize: 11 }}
                      />
                    )}
                  </Flex>
                }
              />
              <InfoRow label="Address" value={client.address} />
            </div>
            <div>
              <InfoRow
                label="Email"
                value={
                  <Flex align="center" gap={4}>
                    <Text
                      style={{
                        fontSize: 16,
                        wordBreak: 'break-all',
                      }}
                    >
                      {client.email}
                    </Text>
                    <CopyOutlined
                      onClick={() =>
                        navigator.clipboard.writeText(client.email)
                      }
                      style={{
                        cursor: 'pointer',
                        color: 'var(--crm-text-secondary)',
                        fontSize: 11,
                      }}
                    />
                    {client.isEmailConfirmed ? (
                      <CheckCircleOutlined
                        style={{ color: 'var(--crm-accent-green, #22c55e)', fontSize: 11 }}
                      />
                    ) : (
                      <CloseCircleOutlined
                        style={{ color: 'var(--crm-accent-red, #ef4444)', fontSize: 11 }}
                      />
                    )}
                  </Flex>
                }
              />
              <InfoRow label="Phone" value={client.phone} />
              <InfoRow
                label="Date of Birth"
                value={
                  client.dateOfBirth
                    ? dayjs(client.dateOfBirth)
                        .locale('en')
                        .format('DD MMM YYYY')
                    : null
                }
              />
              <InfoRow
                label="Language"
                value={client.language?.toUpperCase()}
              />
              <InfoRow
                label="Products"
                value={
                  client.products && client.products.length > 0 ? (
                    <Flex gap={4}>
                      {client.products.map((p) => (
                        <Tag
                          key={p}
                          color={p === 'headway' ? 'blue' : 'purple'}
                          style={{ fontSize: 10, margin: 0 }}
                        >
                          {p}
                        </Tag>
                      ))}
                    </Flex>
                  ) : null
                }
              />
              <InfoRow
                label="Registered At"
                value={
                  client.humanRegisteredAt ||
                  (client.registeredAt
                    ? dayjs(client.registeredAt).format('DD MMM YYYY, HH:mm')
                    : null)
                }
              />
              <InfoRow
                label="Registration Method"
                value={client.socialNetwork || 'Email'}
              />
            </div>
          </div>
        </Section>

        {/* VERIFICATION & STATUS */}
        <Section
          icon={<SafetyCertificateOutlined style={{ color: 'var(--crm-accent-green, #22c55e)' }} />}
          title="Verification & Status"
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10,
              padding: '10px 0',
            }}
          >
            <StatusItem
              label="Identity Verification"
              value={client.verified ? 'Is verified' : 'Not verified'}
              color={client.verified ? 'success' : 'error'}
            />
            <StatusItem
              label="Deposit Status"
              value={client.hasDeposit ? 'Has Deposit' : 'No Deposit'}
              color={client.hasDeposit ? 'success' : 'error'}
            />
            <StatusItem
              label="2FA"
              value={client.isTwoFaActive ? 'On' : 'Off'}
              color={client.isTwoFaActive ? 'success' : 'default'}
              extra={
                client.isTwoFaActive ? (
                  <Guard
                    permission={PERMISSIONS.DISABLING_TWO_FA}
                    mode="permission"
                  >
                    <Edit2FAModal clientId={client.clientId} />
                  </Guard>
                ) : null
              }
            />
            <StatusItem
              label="Auto Deposit"
              value={client.autoDepositDisabled ? 'Manual Only' : 'Enabled'}
              color={!client.autoDepositDisabled ? 'success' : 'default'}
            />
            <StatusItem
              label="Auto Withdrawal"
              value={client.autoWithdrawalStatus ? 'Enabled' : 'Manual Only'}
              color={client.autoWithdrawalStatus ? 'success' : 'default'}
              extra={
                <Guard permission={GUARD_RULES.EDIT_PAYMENTS}>
                  <Button
                    size="small"
                    danger={client.autoWithdrawalStatus}
                    disabled={loadingAutoWDStatus}
                    loading={loadingAutoWDStatus}
                    onClick={() => {
                      submitEditAutoWithdrawalStatus({
                        ...client,
                        autoWithdrawalStatus: !client.autoWithdrawalStatus,
                      })
                    }}
                    style={{ width: 'fit-content', fontSize: 11 }}
                  >
                    {client.autoWithdrawalStatus ? 'Disable' : 'Enable'}
                  </Button>
                </Guard>
              }
            />
            <StatusItem
              label="Premium Status"
              value={client.isPremium ? 'Premium' : 'Standard'}
              color={client.isPremium ? 'gold' : 'default'}
              extra={
                client.isPremium && client.premiumAssignedAt ? (
                  <Text
                    type="secondary"
                    style={{ fontSize: 11 }}
                  >
                    Assigned: {client.humanPremiumAssignedAt}
                    {client.isManualPremium && ' (Manual)'}
                  </Text>
                ) : null
              }
            />
          </div>
        </Section>

        {/* IB PARTNERSHIP */}
        <Section
          icon={<TeamOutlined style={{ color: 'var(--crm-accent-amber, #f59e0b)' }} />}
          title="IB Partnership"
        >
          <div style={{ padding: '10px 0' }}>
            <Flex gap={32} style={{ marginBottom: 12 }}>
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
                >
                  Partner Status
                </Text>
                <Tag
                  color={client.partner ? 'gold' : 'error'}
                  style={{ fontSize: 11 }}
                >
                  {client.partner ? 'Is Partner' : 'Not a Partner'}
                </Tag>
                {client.partner && (
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={path.ib.partners.details(client.clientId)}
                      style={{ fontSize: 12 }}
                      target="_blank"
                    >
                      View Partner Profile
                    </a>
                  </div>
                )}
              </div>

              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
                >
                  Attached To Partner
                </Text>
                {partner?.id ? (
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      <Text style={{ fontSize: 12 }}>
                        <strong>Partner ID:</strong>{' '}
                        <PartnerIdLink partnerId={partner.id} />
                      </Text>
                    </div>
                    <Text style={{ fontSize: 12 }}>
                      <strong>Name:</strong> {partner.firstName}
                    </Text>
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Not attached
                  </Text>
                )}
              </div>
            </Flex>

            {fullPartner && (
              <Guard permission={GUARD_RULES.VIEW_PARTNER_PROFILE}><>
                {/* Partner stats */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4,1fr)',
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  {[
                    { label: 'Active clients', value: fullPartner.activeClients, color: 'var(--crm-accent-blue, #3b82f6)' },
                    { label: 'All clients', value: fullPartner.partnerClientsCount, color: 'var(--crm-accent-cyan, #06b6d4)' },
                    { label: 'Lots', value: fullPartner.sumLots, color: 'var(--crm-accent-purple, #a855f7)' },
                    { label: 'IB Balance', value: fullPartner.withdrawalAmount, color: 'var(--crm-accent-green, #22c55e)' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      style={{
                        background: 'var(--crm-bg-elevated)',
                        border: '1px solid var(--crm-border)',
                        borderRadius: 8,
                        padding: '10px 12px',
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}
                      >
                        {s.label}
                      </Text>
                      <Text
                        strong
                        style={{ fontSize: 16, color: s.color }}
                      >
                        {s.value ?? '0'}
                      </Text>
                    </div>
                  ))}
                </div>

                {/* Partner general info */}
                <div
                  style={{
                    background: 'var(--crm-bg-elevated)',
                    border: '1px solid var(--crm-border)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 12,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 8 }}
                  >
                    Partner general information
                  </Text>
                  {[
                    { label: 'ID', value: fullPartner.id },
                    { label: 'Key', value: fullPartner.key },
                    { label: 'Email', value: fullPartner.email || fullPartner.client?.email },
                    { label: 'Level', value: fullPartner.status },
                    { label: 'From trading', value: fullPartner.sumCommissions },
                    ...(fullPartner.sumCommissionsLevelTwo != null
                      ? [{ label: 'From partner L2', value: fullPartner.sumCommissionsLevelTwo }]
                      : []),
                    ...(fullPartner.sumCommissionsLevelThree != null
                      ? [{ label: 'From partner L3', value: fullPartner.sumCommissionsLevelThree }]
                      : []),
                  ].map(({ label, value }) =>
                    value !== null && value !== undefined && value !== '' ? (
                      <Flex
                        key={label}
                        justify="space-between"
                        style={{ padding: '4px 0', borderBottom: '1px solid var(--crm-border)' }}
                      >
                        <Text type="secondary" style={{ fontSize: 16 }}>{label}</Text>
                        <Text style={{ fontSize: 16 }}>{String(value)}</Text>
                      </Flex>
                    ) : null
                  )}
                </div>
              </></Guard>
            )}

            <Flex gap={8}>
              <Guard permission={GUARD_RULES.ATTACH_PARTNER_TO_CLIENT}>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<LinkOutlined />}
                  onClick={() => toggleModals('isAttachModalOpen', true)}
                >
                  {partner?.id ? 'Change Partner' : 'Attach Partner'}
                </Button>
                {errorAttachClientToPartner.code !== 0 && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {errorAttachClientToPartner.message}
                  </Text>
                )}
                <AttachPartnerToClientModal
                  clientId={client.clientId}
                  isVisible={isOpenModal.isAttachModalOpen}
                  onClose={() => toggleModals('isAttachModalOpen', false)}
                />
              </Guard>

              <Guard permission={GUARD_RULES.DETACH_PARTNER_FROM_CLIENT}>
                {partner?.id && (
                  <>
                    <Button
                      size="small"
                      danger
                      onClick={() =>
                        toggleModals('isDetachModalOpen', true)
                      }
                    >
                      Remove Partner
                    </Button>
                    {errorDetachClientToPartner.code !== 0 && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {errorDetachClientToPartner.message}
                      </Text>
                    )}
                  </>
                )}
                <ConfirmDetachModal
                  isOpen={isOpenModal.isDetachModalOpen}
                  onSubmit={() => {
                    submitDetachClientFromPartner({
                      partnerId: Number(partner.id),
                      clientId: Number(client.clientId),
                    })
                    toggleModals('isDetachModalOpen', false)
                  }}
                  onCancel={() => toggleModals('isDetachModalOpen', false)}
                />
              </Guard>
            </Flex>
          </div>
        </Section>

        {/* SETTINGS */}
        <Section
          icon={<SettingOutlined style={{ color: 'var(--crm-text-secondary)' }} />}
          title="Settings"
        >
          <div style={{ padding: '10px 0' }}>
            <Flex justify="space-between" align="center">
              <Text style={{ fontSize: 12 }}>View Strategies</Text>
              <Tag
                color={client.isViewStrategies ? 'success' : 'error'}
                style={{ fontSize: 11 }}
              >
                {client.isViewStrategies ? 'Allowed' : 'Restricted'}
              </Tag>
            </Flex>
          </div>
        </Section>
      </div>

      {/* RIGHT COLUMN — COMMENTS */}
      <div
        style={{
          background: 'var(--crm-bg)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          overflow: 'hidden',
          position: 'sticky',
          top: 70,
        }}
      >
        <Flex
          align="center"
          gap={8}
          style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--crm-border)',
          }}
        >
          <CommentOutlined style={{ color: 'var(--crm-accent-blue, #3b82f6)' }} />
          <Text strong style={{ fontSize: 13 }}>
            Comments
          </Text>
        </Flex>
        <div style={{ padding: 14 }}>
          {client.clientId !== '' && (
            <CommentList entityType="client" entityId={client.clientId} />
          )}
          <div
            style={{
              marginTop: 16,
              borderTop: '1px solid var(--crm-border)',
              paddingTop: 16,
            }}
          >
            <CommentForm type="client" entityId={client.clientId} />
          </div>
        </div>
      </div>
    </div>
  )
}
